import { useEffect, useState } from "react";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  url: string;
}

export default function ShareModal({ open, onClose, title, url }: ShareModalProps) {
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) {
      setMessage("");
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("textarea");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setMessage("链接已复制到剪贴板，可直接分享给好友");
    } catch {
      setMessage("复制失败，请手动选择链接复制");
    }
  };

  const simulate = (label: string) => {
    setMessage(`已生成${label}分享内容（演示效果，未调用平台接口）`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-primary-950/60"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="分享资源"
        className="relative w-full max-w-md bg-background-50 rounded-lg border border-background-200 p-5 md:p-6 animate-pop-in"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-base text-foreground-950">分享这个资源</h3>
            <p className="mt-1 text-xs text-foreground-500 line-clamp-1">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭分享弹窗"
            className="w-8 h-8 rounded-md border border-background-200 flex items-center justify-center text-foreground-500 cursor-pointer hover:bg-background-100 transition-colors shrink-0"
          >
            <i className="ri-close-line" aria-hidden="true"></i>
          </button>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <input
            readOnly
            value={url}
            aria-label="分享链接"
            className="flex-1 min-w-0 h-10 px-3 rounded-md border border-background-300 bg-background-100 text-xs text-foreground-600 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="h-10 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
          >
            复制链接
          </button>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2">
          {[
            { label: "微信好友", icon: "ri-wechat-line" },
            { label: "微博", icon: "ri-weibo-line" },
            { label: "朋友圈", icon: "ri-share-circle-line" },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => simulate(item.label)}
              className="flex flex-col items-center gap-2 py-3 rounded-md border border-background-200 bg-background-50 cursor-pointer hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center text-primary-600">
                <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <span className="text-xs text-foreground-600">{item.label}</span>
            </button>
          ))}
        </div>

        {message && (
          <p className="mt-4 text-xs text-foreground-600 bg-accent-50 border border-accent-200 rounded-md px-3 py-2 flex items-start gap-2">
            <i className="ri-information-line text-accent-600 mt-0.5" aria-hidden="true"></i>
            {message}
          </p>
        )}

        <p className="mt-4 text-[11px] text-foreground-400 leading-5">
          分享功能为演示效果，微信与微博分享不会真实调用平台接口。
        </p>
      </div>
    </div>
  );
}